import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchOrganizationFileScanningStatus } from '@/api/v4/organization-files.api';
import { fetchSpotlightOrganizationFileScanningStatus } from '@/api/v4/spotlight.api';
import { QueryKey } from '@/config/query-client';
import type { File } from '@/features/action-panel/files/file.interface';
import { notify } from '@/ui/snackbar/notify';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';

import { companyIdState } from '../../company.state';
import {
  type CompanyFetchMode,
  useCompanyFetchMode,
} from '../../company-fetch-context';
import { useAtomValue } from 'jotai';

const fileScanningStatusFetchers: Record<
  CompanyFetchMode,
  typeof fetchOrganizationFileScanningStatus
> = {
  general: fetchOrganizationFileScanningStatus,
  spotlight: fetchSpotlightOrganizationFileScanningStatus,
};

export const useCompanyFileScanningStatus = (file: File) => {
  const { t } = useTranslation('default');
  const queryClient = useQueryClient();
  const companyId = useAtomValue(companyIdState);
  const fetchMode = useCompanyFetchMode();
  const [isError, setError] = useState(false);
  const { hasRequiredPermission } = useUserPermissions();

  const fetchFileScanningStatus = async () => {
    if (!companyId) return;
    return fileScanningStatusFetchers[fetchMode]({
      fileId: file.id,
      organizationId: companyId,
    });
  };

  const query = useQuery({
    queryKey: [
      QueryKey.CompanyFileScanningStatus,
      fetchMode,
      companyId,
      file.id,
    ],
    queryFn: fetchFileScanningStatus,
    enabled:
      !!companyId &&
      hasRequiredPermission(PERMISSION.VIEW_FILES) &&
      file.scanningStatus === 'unscanned',
    staleTime: Infinity,
    refetchInterval: query => {
      const file = query.state.data;
      return !isError && file && file.scanningStatus === 'unscanned'
        ? 1000
        : false;
    },
  });

  const scanningStatus = query.data?.scanningStatus;

  useEffect(() => {
    if (!scanningStatus || scanningStatus === 'unscanned') return;

    void queryClient.invalidateQueries({
      queryKey: [QueryKey.CompanyFiles, fetchMode, companyId],
    });

    if (scanningStatus === 'infected' || scanningStatus === 'error') {
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.CompanyActionsInfo, fetchMode, companyId],
      });
      notify({
        message: t(
          scanningStatus === 'infected'
            ? 'actionPanel.files.infected'
            : 'actionPanel.files.scanningError',
          { name: file.name },
        ),
      });
    }
  }, [scanningStatus, queryClient, fetchMode, companyId, file.name, t]);

  useEffect(() => {
    if (query.isError) {
      setError(true);
      notify({ message: t`unknownError` });
    }
  }, [query.isError, t]);

  return query;
};
